/**
 * Guards for Generate AI Steps / abort / reconnect so stale WebSocket events
 * from a previous run cannot corrupt the current UI (QPN-2087).
 */

export const USER_ABORTED_EXECUTION_MESSAGE = 'Execution Aborted by User';

/**
 * Extract a run correlation id from an agent WebSocket payload.
 * @param {unknown} payload
 * @returns {string|number|null}
 */
export function extractGenerationEventRunId(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const id =
    payload.sessionId ??
    payload.testId ??
    payload.uniqueId ??
    payload.runId ??
    null;
  if (id == null || id === '') return null;
  return id;
}

/**
 * Whether a generation/streaming event should mutate UI for the active run.
 *
 * - No active run id (stopped / idle) → reject everything (blocks late testComplete).
 * - Event run id present and mismatch → reject (previous run).
 * - Event run id present and match → accept.
 * - Event has no run id → accept only while a run is active (logs/screenshots),
 *   unless `requireEventId` is true (use for testComplete when possible).
 *
 * @param {string|number|null|undefined} activeGenerationId
 * @param {unknown} payload
 * @param {{ requireEventId?: boolean }} [options]
 * @returns {boolean}
 */
export function shouldApplyGenerationEvent(activeGenerationId, payload, options = {}) {
  const { requireEventId = false } = options;
  if (activeGenerationId == null || activeGenerationId === '') return false;

  const eventId = extractGenerationEventRunId(payload);
  if (eventId == null || eventId === '') {
    return !requireEventId;
  }
  return String(eventId) === String(activeGenerationId);
}

/**
 * Overall status shown when the user aborts generate/replay.
 * @param {string} [message]
 * @returns {{ status: 'partial', message: string }}
 */
export function buildUserAbortedOverallStatus(message = USER_ABORTED_EXECUTION_MESSAGE) {
  return { status: 'partial', message };
}
